import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ImageIcon, X, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * /admin/black-1of1/reports — Phase 2 admin moderation queue.
 * Mirrors the OwnershipClaimsAdmin pattern (filter tabs, table row,
 * review modal with inline private image preview + approve/reject).
 * Never surfaces internal report status on the public tracker.
 */
export default function AdminBlackParallelReports() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [reviewing, setReviewing] = useState(null);

  const load = async () => {
    setRows(null);
    try {
      const r = await api.get("/admin/black-1of1/reports", { params: status ? { status } : {} });
      setRows(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setRows([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const filters = [
    { key: "pending", label: "Pending", count: counts.pending },
    { key: "approved", label: "Approved", count: counts.approved },
    { key: "rejected", label: "Rejected", count: counts.rejected },
    { key: "", label: "All" },
  ];

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="admin-black-reports">
      <div className="container-gm py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1">Admin · Community Sightings</div>
            <h1 className="font-display text-3xl md:text-4xl">Black 1/1 Reports</h1>
            <p className="text-sm text-neutral-600 mt-1">Moderate community-submitted sightings. Approving a valid sighting marks the sticker FOUND on the public tracker.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1 p-1 rounded-2xl border-2 border-[var(--ink)] bg-white mb-4 w-fit" data-testid="admin-reports-filters">
          {filters.map((f) => (
            <button
              key={f.key || "all"}
              onClick={() => setStatus(f.key)}
              data-testid={`admin-reports-filter-${f.key || "all"}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-widest ${
                status === f.key
                  ? "bg-[var(--ink)] text-[var(--panini-yellow)]"
                  : "text-[var(--ink)] hover:bg-[var(--panini-yellow-soft)]"
              }`}
            >
              {f.label}{typeof f.count === "number" ? ` (${f.count})` : ""}
            </button>
          ))}
        </div>

        {!rows ? (
          <div className="text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-neutral-500 p-6 rounded-2xl border-2 border-dashed border-neutral-200" data-testid="admin-reports-empty">
            No reports match this filter.
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[var(--smoke)] text-xs uppercase tracking-widest bg-[var(--panini-yellow-soft)]">
                <tr>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Sticker</th>
                  <th className="text-left px-4 py-3">Submitter</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Owns?</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--line)] hover:bg-[var(--panini-yellow-soft)]" data-testid={`admin-reports-row-${r.id}`}>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-[var(--panini-red)] font-bold">{r.sticker_number}</div>
                      <div className="text-[11px] text-neutral-500">{r.sticker_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{r.submitter_display_name || "—"}</div>
                      <div className="text-[11px] text-neutral-500">{r.submitter_email || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs uppercase font-mono tracking-widest text-neutral-600">{r.source_type}</div>
                      <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-[var(--panini-red)] underline break-all inline-flex items-center gap-1 max-w-[220px] truncate" data-testid={`admin-reports-source-${r.id}`}>
                        {r.source_url} <ExternalLink size={10} />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {r.owns_sticker ? <span className="text-emerald-700 font-bold">YES</span> : <span className="text-neutral-400">no</span>}
                    </td>
                    <td className="px-4 py-3 text-neutral-500 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setReviewing(r)}
                        data-testid={`admin-reports-open-${r.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--panini-yellow)] font-bold"
                      >
                        {r.status === "pending" ? "Review" : "View"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {reviewing && (
          <ReviewModal report={reviewing} onClose={() => setReviewing(null)}
                       onDone={() => { setReviewing(null); load(); }} />
        )}
      </div>
    </div>
  );
}

function ReviewModal({ report, onClose, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blob = null;
    if (report.image_media_id) {
      api.get(`/admin/black-1of1/reports/${report.id}/image`, { responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          blob = URL.createObjectURL(r.data);
          setImgUrl(blob);
        })
        .catch(() => setImgErr(true));
    }
    return () => { cancelled = true; if (blob) URL.revokeObjectURL(blob); };
  }, [report.id, report.image_media_id]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/black-1of1/reports/${report.id}/${decision}`, {
        review_notes: notes.trim() || null,
      });
      toast.success(decision === "approve" ? "Sighting approved. Sticker marked FOUND." : "Sighting rejected.");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const pending = report.status === "pending";

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="admin-reports-modal">
      <div className="w-full max-w-3xl bg-white border-2 border-[var(--ink)] rounded-2xl my-8 shadow-[8px_8px_0_var(--ink)]">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[var(--ink)]">
          <div>
            <div className="font-display text-2xl">Review sighting</div>
            <div className="text-xs text-neutral-500 font-mono mt-1">{report.sticker_number} · {report.sticker_name}</div>
          </div>
          <button onClick={onClose} data-testid="admin-reports-close" className="text-neutral-500 hover:text-black"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl border-2 border-[var(--ink)] bg-neutral-900 overflow-hidden aspect-[4/3] flex items-center justify-center" data-testid="admin-reports-image-wrap">
              {imgUrl ? (
                <img src={imgUrl} alt="Sighting evidence" className="max-h-full max-w-full object-contain" data-testid="admin-reports-image" />
              ) : imgErr || !report.image_media_id ? (
                <div className="text-white/70 text-sm text-center px-4">
                  <ImageIcon size={28} className="mx-auto mb-2 opacity-60" />
                  {report.image_media_id ? "Image unavailable." : "No image submitted."}
                </div>
              ) : (
                <div className="text-white/60 text-xs">Loading…</div>
              )}
            </div>
            <div className="text-sm space-y-3">
              <Row label="Sticker">{report.sticker_number} · {report.sticker_name} ({report.sticker_country})</Row>
              <Row label="Submitter">
                <div className="font-semibold">{report.submitter_display_name || "—"}</div>
                <div className="text-xs text-neutral-500">{report.submitter_email}</div>
              </Row>
              <Row label="Source type">{report.source_type}</Row>
              <Row label="Source URL">
                <a href={report.source_url} target="_blank" rel="noreferrer" className="text-[var(--panini-red)] underline break-all inline-flex items-center gap-1">
                  {report.source_url} <ExternalLink size={12} />
                </a>
              </Row>
              {report.public_account && <Row label="Public account / seller">{report.public_account}</Row>}
              {report.date_seen && <Row label="Date seen">{report.date_seen}</Row>}
              <Row label="Owns sticker">{report.owns_sticker ? <span className="text-emerald-700 font-bold">YES</span> : "no"}</Row>
              {report.image_media_id && (
                <Row label="Publishing permission">
                  {report.display_permission_granted ? (
                    <span className="text-emerald-700 font-bold">GRANTED</span>
                  ) : (
                    <span className="text-red-700 font-bold">NOT GRANTED — image cannot auto-publish</span>
                  )}
                </Row>
              )}
              {report.photo_credit && (
                <Row label="Photo credit">
                  <span className="italic">{report.photo_credit}</span>
                </Row>
              )}
              <Row label="Status"><StatusPill status={report.status} /></Row>
              <Row label="Submitted">{new Date(report.created_at).toLocaleString()}</Row>
              {report.moderated_at && <Row label="Moderated">{new Date(report.moderated_at).toLocaleString()}</Row>}
              {report.moderation_notes && (
                <Row label="Prior admin notes">
                  <div className="text-sm bg-[var(--panini-yellow-soft)] rounded-lg px-3 py-2">{report.moderation_notes}</div>
                </Row>
              )}
            </div>
          </div>

          {report.notes && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Reporter notes</div>
              <div className="text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 whitespace-pre-wrap">{report.notes}</div>
            </div>
          )}

          {pending && (
            <>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-2">Admin notes (optional)</div>
                <textarea
                  data-testid="admin-reports-review-notes"
                  className="w-full px-3 py-2.5 rounded-lg border-2 border-neutral-300 focus:border-[var(--ink)] focus:outline-none text-sm"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                  placeholder="Why this is being approved or rejected…"
                />
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <button
                  onClick={() => decide("approve")}
                  disabled={busy}
                  data-testid="admin-reports-approve"
                  className="flex-1 px-6 py-3 rounded-full font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: "#16a34a" }}
                >
                  <CheckCircle2 size={16} /> Approve &amp; mark FOUND
                </button>
                <button
                  onClick={() => decide("reject")}
                  disabled={busy}
                  data-testid="admin-reports-reject"
                  className="px-6 py-3 rounded-full border-2 border-[var(--panini-red)] text-[var(--panini-red)] hover:bg-[var(--panini-red)] hover:text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <XCircle size={16} /> Reject
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-0.5">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    approved: { bg: "#dcfce7", fg: "#166534", Icon: CheckCircle2, label: "Approved" },
    pending:  { bg: "#fef3c7", fg: "#92400e", Icon: Clock,        label: "Pending" },
    rejected: { bg: "#fee2e2", fg: "#991b1b", Icon: XCircle,      label: "Rejected" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
          style={{ background: cfg.bg, color: cfg.fg }}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
}
